/**
 * Utility functions for image processing and metadata extraction
 */

/**
 * Calculates the actual DPI (dots per inch) from an image file and surface dimensions
 * @param file - The image file to analyze
 * @param surface - The surface object containing PPI and bounding box information
 * @returns Promise<number> - The calculated DPI value
 */
export const calculateImageDpi = (
  file: File,
  surface?: { ppi: number; boundingBox?: { width: number; height: number } }
): Promise<number> => {
  if (!surface?.ppi) {
    // Fallback to standard DPI if surface PPI is not available
    return Promise.resolve(300);
  }

  // Create a temporary image to get dimensions
  return new Promise<number>(resolve => {
    const img = new Image();
    const blobUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      // Calculate DPI based on surface dimensions and image pixels
      // DPI = pixels / inches
      const surfaceWidthInches = (surface.boundingBox?.width ?? 0) / surface.ppi;
      const surfaceHeightInches = (surface.boundingBox?.height ?? 0) / surface.ppi;

      if (surfaceWidthInches > 0 && surfaceHeightInches > 0) {
        // Use the smaller dimension to calculate DPI (more conservative estimate)
        const dpiX = img.naturalWidth / surfaceWidthInches;
        const dpiY = img.naturalHeight / surfaceHeightInches;
        const dpi = Math.min(dpiX, dpiY);

        // Round to nearest whole number and ensure reasonable bounds
        const roundedDpi = Math.round(Math.max(72, Math.min(600, dpi)));
        resolve(roundedDpi);
      } else {
        resolve(300); // Fallback
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      resolve(300);
    };
    img.src = blobUrl;
  });
};
